import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLang } from "@/contexts/LanguageContext";
import { LANGUAGES } from "@/lib/i18n";

const Header = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { id: "home", to: "/#home", label: t.nav.home },
    { id: "structure", to: "/#about", label: t.nav.structure },
    { id: "gallery", to: "/#gallery", label: t.nav.gallery || "Galleria" },
    { id: "rooms", to: "/#rooms", label: t.nav.rooms },
    { id: "services", to: "/#services", label: t.nav.services },
    { id: "attractions", to: "/#attractions", label: t.nav.attractions },
    { id: "directions", to: "/#directions", label: t.nav.directions },
    { id: "contact", to: "/#contact", label: t.nav.contact },
  ];

  const handleNav = (e, to) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const id = to.split("#")[1];
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const id = to.split("#")[1];
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const currentLang = LANGUAGES.find((l) => l.code === lang);

  return (
    <header
      className="glass-header fixed top-0 left-0 right-0 z-50 border-b border-[#E8E2D9]"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img
            src="https://customer-assets.emergentagent.com/job_accommodation-plus/artifacts/quheoms4_WhatsApp%20Image%202026-05-01%20at%2014.58.40.jpeg"
            alt="B&B Emmanuel"
            className="h-16 md:h-20 w-auto object-contain"
            style={{ mixBlendMode: "multiply", filter: "contrast(1.04)" }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={item.to}
              onClick={(e) => handleNav(e, item.to)}
              data-testid={`nav-${item.id}`}
              className="text-sm font-medium text-[#2D2926] hover:text-[#AC5D45] transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-md border border-[#E8E2D9] hover:border-[#AC5D45] transition"
                data-testid="lang-switcher"
              >
                <span>{currentLang?.flag}</span>
                <span>{currentLang?.label}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className="cursor-pointer"
                >
                  <span className="mr-2">{l.flag}</span>
                  {l.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            onClick={() => navigate("/booking")}
            data-testid="header-book-btn"
            className="hidden sm:inline-flex bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full px-5 h-10"
          >
            {t.nav.book}
          </Button>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E8E2D9] bg-white">
          <div className="px-5 py-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={item.to}
                onClick={(e) => handleNav(e, item.to)}
                data-testid={`mobile-nav-${item.id}`}
                className="text-base font-medium text-[#2D2926] py-2"
              >
                {item.label}
              </a>
            ))}
            <Button
              onClick={() => {
                setOpen(false);
                navigate("/booking");
              }}
              data-testid="mobile-book-btn"
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full mt-2"
            >
              {t.nav.book}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
